/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import style from './transaction.module.css';
import { Table, Input, Pagination, Space, Dropdown, Checkbox, Tag, message, Upload, Button, DatePicker } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { CiMenuKebab, CiFilter, CiSearch } from 'react-icons/ci';
import ButtonP from '../../components/Basic/button';
import FormModal from '../../components/Modal/FormModal';
import Image from '../../Images/profile_image.png'
import * as XLSX from 'xlsx';
import { CiImport, CiExport } from "react-icons/ci";
import Header from '../../components/Header/header';
import { Select } from 'antd';
import * as Action from '../../store/slices/transaction/transaction'
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;

interface Api {
    getTransaction: (params: any) => void;
    getUploadedFiles: () => Promise<any>;
    Transaction_Red: any;
}


interface DataSource {
    key: string;
    ReferenceID: string;
    Type: string;
    From: string;
    To: string;
    Status: string;
    Date: string;
    Name: string;
    Amount: string;
}

// Date fields can arrive either as a plain ISO string or as an object
// { minutesOffset, timestamp, offsetDateTime } — normalize to an ISO string.
const extractDateString = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object") return value.offsetDateTime || value.timestamp || null;
    return null;
};

const MultiViewTable: React.FC<Api> = ({ getTransaction, getUploadedFiles, Transaction_Red }) => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(9);
    const [uploadedFiles, setUploadedFiles] = useState<{ id: number; name: string }[]>([]);
    const [selectedColumns, setSelectedColumns] = useState<string[]>(['ReferenceID', 'From', 'Status', 'Date', 'Name', 'Amount']);
    const [loadingText, setLoadingText] = useState<boolean>(true)
    const [buttonLoading, setButtonLoading] = useState<boolean>(false);
    const [open, setOpen] = useState(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [modalText, setModalText] = useState<string>('');
    const [tableData, setTableData] = useState<DataSource[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [filters, setFilters] = useState({
        fromDate: undefined as string | undefined,
        toDate: undefined as string | undefined,
        minTrxAmount: 0,
        maxTrxAmount: 0,
        customerIdentifier: "",
        branchIdentifier: "",
        accountShortName: "",
        fileId: undefined as number | undefined,
    });



    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchText);
            if (searchText) {
                setCurrentPage(1); // Reset to page 1 when search text changes
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        getUploadedFiles().then((res: any) => {
            if (res?.success && Array.isArray(res?.data)) {
                setUploadedFiles(
                    res.data
                        .filter((f: any) => f.isApproved === true)
                        .map((f: any) => ({ id: f.fileId, name: f.fileName }))
                );
            }
        });
    }, []);



    useEffect(() => {
        fetchTransactions(currentPage, pageSize);
    }, [currentPage, pageSize, filters, debouncedSearch]);

    const fetchTransactions = (page: number, size: number) => {
        setLoadingText(true);
        
        // Clear old data to prevent stacking
        setTableData([]);

        const params = {
            page: page - 1,
            size,
            bankTransactionId: debouncedSearch || undefined,
            fileId: filters.fileId,
            fromDate: filters.fromDate || undefined,
            toDate: filters.toDate || undefined,
        };

        const cleanParams: any = Object.fromEntries(
            Object.entries(params).filter(([_, v]) => v !== "" && v !== undefined && v !== null)
        );

        console.log('🔍 Fetching transactions with params:', cleanParams);
        getTransaction(cleanParams);
    };



    useEffect(() => {
        const apiResponse = Transaction_Red?.data?.[0]?.res;
        const errorResponse = Transaction_Red?.dataSingle?.[0]?.res;

        // Enhanced debugging
        console.log('=== Transaction Redux State ===');
        console.log('Full Redux State:', Transaction_Red);
        console.log('Loading State:', Transaction_Red?.loading);
        console.log('API Response:', apiResponse);
        console.log('Error Response:', errorResponse);
        console.log('Current Table Data Length:', tableData.length);

        // Check for error responses first
        if (errorResponse?.status === '1004') {
            console.log('❌ Session expired');
            message.error('Session expired. Please login again.');
            localStorage.clear();
            navigate('/');
            setLoadingText(false);
            return;
        }

        if (errorResponse?.status === '1005') {
            console.log('⚠️ No transactions found');
            message.warning('No transactions found.');
            setTableData([]);
            setTotal(0);
            setLoadingText(false);
            return;
        }

        // If no response yet, keep loading
        if (!apiResponse) {
            console.log('⏳ Waiting for API response...');
            return;
        }

        console.log('Response Status:', apiResponse?.status);
        console.log('Response Data:', apiResponse?.data);
        console.log('Has Content:', apiResponse?.data?.content);
        console.log('Is Array:', Array.isArray(apiResponse?.data?.content));

        // Process successful response
        if (apiResponse?.status === "200" && apiResponse?.data?.content && Array.isArray(apiResponse.data.content)) {
            console.log('✅ Mapping paginated data, count:', apiResponse.data.content.length);
            console.log('✅ Total elements:', apiResponse.data.totalElements);
            
            const mapped = apiResponse.data.content.map((item: any) => {
                const transactionDate = extractDateString(item.transactionDate);
                return {
                    key: item.transactionId,
                    ReferenceID: item.bankTransactionId || "-",
                    Type: item.accountShortName || "-",
                    From: item.customerIdentifier || "-",
                    To: item.branchIdentifier || "-",
                    Status: "Active",
                    Date: transactionDate
                        ? new Date(transactionDate).toLocaleDateString("en-GB")
                        : "-",
                    Name: item.accountShortName || "-",
                    Amount: item.transactionAmount != null ? `OMR ${item.transactionAmount.toLocaleString()}` : "-",
                };
            });

            console.log('✅ Mapped data:', mapped);
            console.log('✅ Setting table data with', mapped.length, 'items');
            setTableData(mapped);
            setTotal(apiResponse.data.totalElements || 0);
            setLoadingText(false);
        } else if (apiResponse?.status === "200" && Array.isArray(apiResponse?.data)) {
            console.log('✅ Mapping array data, count:', apiResponse.data.length);
            const mapped = apiResponse.data.map((item: any) => {
                const transactionDate = extractDateString(item.transactionDate);
                return {
                    key: item.transactionId,
                    ReferenceID: item.bankTransactionId || "-",
                    Type: item.accountShortName || "-",
                    From: item.customerIdentifier || "-",
                    To: item.branchIdentifier || "-",
                    Status: "Active",
                    Date: transactionDate
                        ? new Date(transactionDate).toLocaleDateString("en-GB")
                        : "-",
                    Name: item.accountShortName || "-",
                    Amount: item.transactionAmount != null ? `OMR ${item.transactionAmount.toLocaleString()}` : "-",
                };
            });

            console.log('✅ Mapped data:', mapped);
            setTableData(mapped);
            setTotal(apiResponse.noOfRecord || 0);
            setLoadingText(false);
        } else if (apiResponse && apiResponse.status !== "200") {
            console.log('❌ API Error:', apiResponse.status, apiResponse.message);
            message.error(apiResponse.message || 'Failed to load transactions');
            setTableData([]);
            setTotal(0);
            setLoadingText(false);
        } else {
            console.log('⚠️ Unexpected response format');
            console.log('apiResponse:', JSON.stringify(apiResponse, null, 2));
            setLoadingText(false);
        }
    }, [Transaction_Red, navigate]);


    const onSearch = (value: string) => {
        setSearchText(value);
        // Page reset will happen in debounce useEffect
    };

    const handleCheckboxChange = (checked: boolean, column: string) => {
        setSelectedColumns((prevSelected) =>
            checked ? [...prevSelected, column] : prevSelected.filter((col) => col !== column)
        );
    };



    const Addhandle = () => {
        setShowForm(true);
    };




    const Backhandle = () => {
        setShowForm(false); // Show the table and hide the form
    };




    const handleOpen = () => {
        setModalText('Are you sure you want to delete this record?');
        setOpen(true);
    }




    const actionMenu = (
        <Space direction="vertical" className={style.actions}>
            <div className={style.dropdownItem}>Edit</div>
            <div className={style.dropdownItem} onClick={handleOpen}>Delete</div>
        </Space>
    );




    const allColumns = [
        {
            title: 'Transaction ID',
            dataIndex: 'ReferenceID',
            key: 'ReferenceID',
            width: 150,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.ReferenceID || !b.ReferenceID) return 0;
                return a.ReferenceID.localeCompare(b.ReferenceID);
            },
        },
        // {
        //     title: 'Transaction Type',
        //     dataIndex: 'Type',
        //     key: 'Type',
        //     width: 150,
        //     sorter: (a: DataSource, b: DataSource) => a.Type.localeCompare(b.Type),
        // },
        {
            title: 'Customer Identifier',
            dataIndex: 'From',
            key: 'From',
            width: 100,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.From || !b.From) return 0;
                return a.From.localeCompare(b.From);
            },
        },

        {
            title: 'Date',
            dataIndex: 'Date',
            key: 'Date',
            width: 100,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.Date || !b.Date) return 0;
                return a.Date.localeCompare(b.Date);
            },
        },
        // {
        //     title: 'Time',
        //     dataIndex: 'Time',
        //     key: 'Time',
        //     width: 100,
        //     sorter : (a : DataSource  , b: DataSource) => {
        //         if (!a.Time || !b.Time) return 0;
        //         return a.Time.localeCompare(b.Time);
        //     },
        // },
        {
            title: 'Amount',
            dataIndex: 'Amount',
            key: 'Amount',
            width: 100,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.Amount || !b.Amount) return 0;
                return a.Amount.localeCompare(b.Amount);
            },
        },
        {
            title: 'Customer Name',
            dataIndex: 'Name',
            key: 'Name',
            width: 150,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.Name || !b.Name) return 0;
                return a.Name.localeCompare(b.Name);
            },
        },

        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'Status',
            width: 100,
            sorter: (a: DataSource, b: DataSource) => {
                if (!a.Status || !b.Status) return 0;
                return a.Status.localeCompare(b.Status);
            },
            render: (Status: string) => {
                const color = Status === "Active" ? "cyan" : Status === "Pending" ? "yellow" : "red";
                return (
                    <Tag style={{ marginInlineEnd: 0 }} color={color}>
                        {Status.toUpperCase()}
                    </Tag>
                )

            },
        },
        // {
        //     title: 'Action',
        //     key: 'action',
        //     fixed: 'right' as const,
        //     width: 50,
        //     render: () => (
        //         <Dropdown
        //             overlay={actionMenu}
        //             trigger={['click']}
        //             placement="bottomRight"
        //         >
        //             <CiMenuKebab className={style.actionIcon} />
        //         </Dropdown>
        //     ),
        // },
    ];





    const filteredColumns = allColumns.filter((col) => selectedColumns.includes(col.key) || col.key === 'action');

    // Use tableData directly - search is now server-side
    const filteredData = tableData;

    const items = [
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('ReferenceID')}
                    value={'ReferenceID'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'ReferenceID')}
                >
                    Reference ID
                </Checkbox>
            ),
            key: '2',
        },
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('From')}
                    value={'From'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'From')}
                >
                    From
                </Checkbox>
            ),
            key: '3',
        },
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('Status')}
                    value={'Status'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'Status')}
                >
                    Status
                </Checkbox>
            ),
            key: '5',
        },
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('Date')}
                    value={'Date'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'Date')}
                >
                    Date
                </Checkbox>
            ),
            key: '6',
        },
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('Name')}
                    value={'Name'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'Name')}
                >
                    Customer Name
                </Checkbox>
            ),
            key: '7',
        },
        {
            label: (
                <Checkbox
                    checked={selectedColumns.includes('Amount')}
                    value={'Amount'}
                    onChange={(e) => handleCheckboxChange(e.target.checked, 'Amount')}
                >
                    Amount
                </Checkbox>
            ),
            key: '8',
        },

    ];












    const handleExport = async () => {
        setButtonLoading(true);
        message.loading('Preparing export...', 0);

        try {
            const token = localStorage.getItem('loginToken');
            if (!token) {
                message.destroy();
                message.error('Not authenticated. Please login.');
                setButtonLoading(false);
                return;
            }

            const apiUrl = process.env.REACT_APP_API_URL;

            // Reuse the currently active table filters/search for the export
            const queryParams: Record<string, string> = { direction: 'ASC' };
            if (filters.fromDate) queryParams.fromDate = filters.fromDate;
            if (filters.toDate) queryParams.toDate = filters.toDate;
            if (filters.minTrxAmount) queryParams.minTrxAmount = String(filters.minTrxAmount);
            if (filters.maxTrxAmount) queryParams.maxTrxAmount = String(filters.maxTrxAmount);
            if (debouncedSearch) queryParams.bankTransactionId = debouncedSearch;
            if (filters.customerIdentifier) queryParams.customerIdentifier = filters.customerIdentifier;
            if (filters.branchIdentifier) queryParams.branchIdentifier = filters.branchIdentifier;
            if (filters.accountShortName) queryParams.accountShortName = filters.accountShortName;
            if (filters.fileId !== undefined && filters.fileId !== null) queryParams.fileId = String(filters.fileId);

            const resp = await fetch(`${apiUrl}/api/trx/export?${new URLSearchParams(queryParams).toString()}`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (resp.status !== 200) {
                const err = await resp.json().catch(() => ({}));
                message.destroy();
                message.error(err.message || 'Failed to export transactions');
                setButtonLoading(false);
                return;
            }

            const blob = await resp.blob();

            // Prefer the server-provided filename (Content-Disposition), fall back to a local one
            const disposition = resp.headers.get('content-disposition') || '';
            const match = disposition.match(/filename="?([^";]+)"?/i);
            const selectedFile = uploadedFiles.find(f => f.id === filters.fileId);
            const fileSuffix = selectedFile ? `_${selectedFile.name.replace(/[^a-zA-Z0-9]/g, '_')}` : '';
            const fallbackName = `transactions${fileSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
            const fileName = match?.[1] || fallbackName;

            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);

            message.destroy();
            message.success('Transactions exported successfully!');
        } catch (error) {
            message.destroy();
            message.error('Failed to export data. Please try again.');
        } finally {
            setButtonLoading(false);
        }
    };


    

    const handleUpload = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const binaryStr = e.target?.result;
            const workbook = XLSX.read(binaryStr, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const data = XLSX.utils.sheet_to_json(worksheet);

            console.log("Uploaded Data:", data);
            message.success("File uploaded and processed successfully!");
        };
        reader.readAsBinaryString(file);

        return false; // Prevent default upload behavior
    };

    const uploadProps = {
        beforeUpload: handleUpload,
        accept: ".xlsx,.csv",
    };





    return (
        <div className={style.sroll}>
            <Header />
            <FormModal modalTitle={modalText} Subtitle={modalText} open={open} setOpen={setOpen} onOk={() => console.log('Form submitted!')} onCancel={() => console.log('Modal cancelled')} />
            {showForm ? (
                // <Features Back={Backhandle} />
                ""
            ) : (
                // Render the table when not showing the form
                <>
                    <div className={style.Route}>
                        <h5>Transactions</h5>
                        <div className={style.btns}>
                            <Button
                                type="primary"
                                loading={buttonLoading}
                                className={style.createBtn2}
                                onClick={handleExport}
                            >
                                <CiExport />
                                Data Export
                            </Button>
                            {/* <Upload {...uploadProps} >
                                <Button type="primary" className={style.createBtn}>
                                    <CiImport />
                                    Data Import
                                </Button>
                            </Upload> */}
                        </div>
                    </div>
                    <div className={style.container}>
                        <Space className={style.SearchTable}>
                            <div className={style.SearchBar}>
                                <Input
                                    placeholder="Search by Transaction ID..."
                                    prefix={<CiSearch size={20} />}
                                    onChange={(e) => onSearch(e.target.value)}
                                    value={searchText}
                                    allowClear
                                    style={{ width: 250 }}
                                    className={style.inputSearch}
                                />
                                <Select
                                    allowClear
                                    showSearch
                                    placeholder="Filter by file"
                                    style={{ width: 200 }}
                                    value={filters.fileId}
                                    onChange={(value: number | undefined) => {
                                        console.log('📁 File selected:', value);
                                        setLoadingText(true); // Explicitly set loading
                                        setFilters(prev => ({ ...prev, fileId: value }));
                                        setCurrentPage(1);
                                    }}
                                    filterOption={(input, option) =>
                                        String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                    options={uploadedFiles.map(f => ({ label: f.name, value: f.id }))}
                                />
                                <RangePicker
                                    format="YYYY-MM-DD"
                                    placeholder={['From Date', 'To Date']}
                                    allowClear
                                    style={{ width: 240 }}
                                    onChange={(dates) => {
                                        setFilters(prev => ({
                                            ...prev,
                                            fromDate: dates?.[0]?.format('YYYY-MM-DD') || undefined,
                                            toDate: dates?.[1]?.format('YYYY-MM-DD') || undefined,
                                        }));
                                        setCurrentPage(1);
                                    }}
                                />
                                <Dropdown
                                    menu={{ items }}
                                    trigger={['click']}
                                >
                                    <SettingOutlined className={style.tableSettings} />
                                </Dropdown>
                            </div>
                        </Space>
                        {searchText && tableData.length === 0 && !loadingText && (
                            <div style={{ padding: '10px 0', color: '#ff4d4f', fontSize: 13 }}>
                                No transactions found matching "{searchText}". Try a different search term.
                            </div>
                        )}
                        <Table
                            loading={loadingText}
                            columns={filteredColumns}
                            dataSource={filteredData}
                            pagination={false}
                            rowSelection={{ type: 'checkbox' }}
                            size="small"
                            locale={{
                                emptyText: searchText 
                                    ? `No results found for "${searchText}"` 
                                    : 'No transactions available'
                            }}
                        />
                        <Pagination
                            align="end"
                            showTotal={(total) => `Total ${total} items`}
                            current={currentPage}
                            total={total}
                            pageSize={pageSize}
                            pageSizeOptions={[9, 18, 36, 72]}
                            showSizeChanger
                            onChange={(page, newSize) => {
                                setCurrentPage(page);
                                if (newSize !== pageSize) {
                                    setPageSize(newSize);
                                    setCurrentPage(1); 
                                }
                            }}
                        />
                    </div>
                </>
            )
            }
        </div >
    );
};


function mapStateToProps(state: any) {
    return {
        Transaction_Red: state.Transaction_Red
    }
}

export default connect(mapStateToProps, Action)(MultiViewTable)